import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CommonList from '../common/components/CommonList';
import { ListSelectionMode } from '../common/components/types';
import { stockAuditService } from '../../services/stockAuditService';
import { extractErrorMessage } from '../../utils/errorUtils';
import { formatTimestampDate } from '../../utils/dateTimeUtils';
import type { StockAudit } from '../../types/stockAudit';

const describeScope = (audit: StockAudit): string =>
  audit.productIds.length === 0
    ? 'Весь склад'
    : `Продуктів у фільтрі: ${audit.productIds.length}`;

const describeResult = (audit: StockAudit): string | undefined => {
  if (audit.status === 'IN_PROGRESS') {
    return 'Триває';
  }
  return `Очікувалось: ${audit.expectedCount ?? 0} · Знайдено: ${audit.foundCount ?? 0}`
    + ` · Відсутні: ${audit.missingCount ?? 0} · Зайві: ${audit.unexpectedCount ?? 0}`;
};

export default function StockAuditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) {
    throw new Error('Company ID is required');
  }

  const companyId = parseInt(id);

  const [audits, setAudits] = useState<StockAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    stockAuditService.list(companyId)
      .then((data) => {
        if (!cancelled) {
          setAudits(data);
          setError(null);
        }
      })
      .catch((err) => {
        console.error('Failed to load stock audits:', err);
        if (!cancelled) {
          setError(extractErrorMessage(err, 'Не вдалося завантажити ревізії'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const openAudit = (auditId: number) => {
    navigate(`/companies/${companyId}/stock-audits/${auditId}`);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Ревізія
        </Typography>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/companies/${companyId}/stock-audits/new`)}
        >
          Нова ревізія
        </Button>
      </Box>

      {/* Content */}
      {error && (
        <Typography
          color="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <CommonList<StockAudit>
          items={audits}
          toListItem={(audit) => ({
            id: audit.id,
            primary: audit.stockName ?? `Склад ${audit.stockId}`,
            secondary: `${formatTimestampDate(audit.createdDate)} · ${describeScope(audit)}`,
            tertiary: describeResult(audit),
          })}
          selectionMode={ListSelectionMode.VIEW}
          showSelection={false}
          onItemClick={(audit) => openAudit(audit.id)}
          emptyMessage="Ревізій ще не було"
        />
      )}
    </Box>
  );
}
